import React, { useEffect, useState, useCallback } from "react";
import SurveyListHeader from "../../../component/layout/MainHeader";
import SurveyListSidebar from "../../../component/layout/MainSidebar";
import Svg from "../../../component/Svg";
import Input from "../../../core/Inputs/DefaultInput";
import { Formik } from "formik";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Select from "react-select";
import ExpressionEditor from './components/ExpressionEditor';
import RuleTriggerSummary from './components/RuleTriggerSummary';
import {hasLogicValue,validateExpression} from './components/expressionEditorModel';
import { performHttpRequest } from "../../../../axios/PerformHttpRequest";
import { toast } from "react-toastify";
import { setPlatformAPI, use } from "echarts";
import { getTokenData } from '../../../store/actions/authAction'

export default function Create() {
    const apiUrl = process.env.REACT_APP_NODE_API_URL;
    const { id, cid } = useParams();
    const navigate = useNavigate()
    const [logicName, setLogicName] = useState("");
    const [when, setWhen] = useState([{
        value_type: 'static',
        variable: '',
        operator: '',
        value: '',
        variable_data_type: 'number'
    }]);
    const [whenAndOrOperator, setWhenAndOrOperator] = useState('AND');
    const [then, setThen] = useState([{
        value_type: 'static',
        action: '',
        variable: '',
        operator: '',
        value: '',
        variable_data_type: 'number'
    }]);
    const [roiActionArr, setRoiActionArr] = useState([]);
    const [userId, setuserId] = useState('');
    const [roiName, setRoiName] = useState("");
    
    // Validation state
    const [logicNameError, setLogicNameError] = useState('');
    const [whenErrors, setWhenErrors] = useState([{}]);
    const [thenErrors, setThenErrors] = useState([{}]);
    
    // Bradcums
    const breadcrumbs = JSON.parse(localStorage.getItem('custom-variable'));
    const dispatch = useDispatch()

    // Validation functions
    const validateLogicName = (value) => {
        if (!value || value.trim() === '') {
            return 'Logic name is required';
        }
        if (value.trim().length < 2) {
            return 'Logic name must be at least 2 characters';
        }
        return '';
    };

    const validateWhenCondition = (condition, index) => {
        const errors = {};
        
        if (!condition.variable || condition.variable.trim() === '') {
            errors.variable = 'Condition field is required';
        }
        
        if (!condition.operator || condition.operator.trim() === '') {
            errors.operator = 'Operator is required';
        }
        
        if (!hasLogicValue(condition.value)) {
            errors.value = 'Value is required';
        }
        
        return errors;
    };

    const validateThenAction = (action, index) => {
        const errors = {};
        
        if (!action.action || action.action.trim() === '') {
            errors.action = 'Action is required';
        }
        
        if (!action.variable || action.variable.trim() === '') {
            errors.variable = 'Variable is required';
        }
        
        if (action.action === 'calculate' && (!action.operator || action.operator.trim() === '')) {
            errors.operator = 'Operator is required for calculate action';
        }
        
        if (action.action === 'calculate' || action.action === 'show_error') {
            if (action.value_type==='expression'){
                const message=validateExpression(action.value,roiActionArr);if(message)errors.value=message;
            } else if (!hasLogicValue(action.value)) {
                errors.value = 'Value is required';
            }
        }
        
        return errors;
    };

    const validateForm = () => {
        let isValid = true;
        
        // Validate logic name
        const logicNameValidation = validateLogicName(logicName);
        setLogicNameError(logicNameValidation);
        if (logicNameValidation) isValid = false;
        
        // Validate when conditions
        const whenValidationErrors = when.map((condition, index) => validateWhenCondition(condition, index));
        setWhenErrors(whenValidationErrors);
        if (whenValidationErrors.some(error => Object.keys(error).length > 0)) isValid = false;
        
        // Validate then actions
        const thenValidationErrors = then.map((action, index) => validateThenAction(action, index));
        setThenErrors(thenValidationErrors);
        if (thenValidationErrors.some(error => Object.keys(error).length > 0)) isValid = false;
        
        return isValid;
    };

    // Clear validation errors when user starts typing/selecting
    const clearLogicNameError = () => {
        setLogicNameError('');
    };

    const clearWhenError = (index, field) => {
        const newErrors = [...whenErrors];
        if (newErrors[index]) {
            delete newErrors[index][field];
        }
        setWhenErrors(newErrors);
    };

    const clearThenError = (index, field) => {
        const newErrors = [...thenErrors];
        if (newErrors[index]) {
            delete newErrors[index][field];
        }
        setThenErrors(newErrors);
    };

    useEffect(() => {
        dispatch(getTokenData(setuserId))
        // Fetch ROI name for breadcrumbs
        const fetchRoiDetail = async () => {
            try {
                const response = await performHttpRequest(
                    "GET",
                    `${apiUrl}/roi/getRoiDetail/${id}`,
                    ""
                );
                if (response.data?.statusCode === 200 && response.data?.result === "success") {
                    setRoiName(response.data.data.roi_name);
                }
            } catch (error) {
                // Optionally handle error
            }
        };
        if (id) fetchRoiDetail();
    }, [userId, dispatch, id]);

    const getRoiVariables = async () => {
        // Collect all component types that contribute variables
        const componentTypesCollected = new Set();
        try {
            const response = await performHttpRequest(
                "GET",
                `${apiUrl}/roi-pages/detail/${id}`,
                ""
            );
            const dataStructure = response.data;
            let roi_content = {};
            let roi_global_arr = {};
            let data = dataStructure.result;
            // console.log('data', data);
            for (let i = 0; i < data.length; i++) {
                roi_content[data[i].roi_page_id] = data[i].structure;
                // console.log('data', data[i].structure);
                let data__ = JSON.parse(data[i].structure)['content'];

                // --- Helper: add variables for a DoubleBarChart node regardless of where it appears ---
                const addVarsForDoubleBar = (node, pageId) => {
                    if (!node || typeof node !== 'object') return;
                    if (node.type === 'DoubleBarChart') {
                        componentTypesCollected.add('DoubleBarChart');
                        const chartId = node?.props?.id || 'DoubleBarChart';
                        const rows = node?.props?.Data || [];
                        rows.forEach((row, idx) => {
                            const labelText = (row && typeof row.Label === 'string' && row.Label.trim()) ? row.Label.trim() : `Bar ${idx + 1}`;
                            const labelKey = (row && row.LabelUuid) ? row.LabelUuid : `${chartId}-row-${idx}-label`;
                            const bKey = (row && row.BUuid) ? row.BUuid : `${chartId}-row-${idx}-b`;
                            const labelSlug = labelText.toString().trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase();
                            const categoryKeyNamed = `${chartId}-${labelSlug}-category`;
                            const firstKeyNamed = `${chartId}-${labelSlug}-first_bar_value`;
                            const secondKeyNamed = `${chartId}-${labelSlug}-second_bar_value`;

                            // Label (string)
                            roi_global_arr[labelKey] = {
                                currency: '0',
                                count: 0,
                                actions: '',
                                action_id: '',
                                link: '',
                                value: labelText,
                                default_value: '',
                                text: labelText,
                                name: labelText,
                                variable_data_type: 'string',
                            };

                            // Series B (number)
                            const numericB = parseFloat(row?.B ?? 0) || 0;
                            roi_global_arr[bKey] = {
                                currency: '0',
                                count: 0,
                                actions: '',
                                action_id: '',
                                link: '',
                                value: numericB,
                                default_value: '',
                                text: `${labelText} (B)`,
                                name: `${labelText} (B)`,
                                variable_data_type: 'number',
                            };

                            if (labelSlug) {
                                roi_global_arr[labelSlug] = {
                                    currency: '0',
                                    count: 0,
                                    actions: '',
                                    action_id: '',
                                    link: '',
                                    value: numericB,
                                    default_value: '',
                                    text: labelText,
                                    name: labelText,
                                    variable_data_type: 'number',
                                };
                            }

                            // Named variables to avoid duplicates across charts and make labels explicit
                            roi_global_arr[categoryKeyNamed] = {
                                currency: '0',
                                count: 0,
                                actions: '',
                                action_id: '',
                                link: '',
                                value: labelText,
                                default_value: '',
                                text: `${labelText} Category`,
                                name: `${labelText} Category`,
                                variable_data_type: 'string',
                            };
                            const numericA = parseFloat(row?.A ?? 0) || 0;
                            roi_global_arr[firstKeyNamed] = {
                                currency: '0',
                                count: 0,
                                actions: '',
                                action_id: '',
                                link: '',
                                value: numericA,
                                default_value: '',
                                text: `${labelText} First bar Value`,
                                name: `${labelText} First bar Value`,
                                variable_data_type: 'number',
                            };
                            roi_global_arr[secondKeyNamed] = {
                                currency: '0',
                                count: 0,
                                actions: '',
                                action_id: '',
                                link: '',
                                value: numericB,
                                default_value: '',
                                text: `${labelText} Second bar Value`,
                                name: `${labelText} Second bar Value`,
                                variable_data_type: 'number',
                            };
                        });
                    }
                };

                // --- Helper: recursive traversal through any node/array structure ---
                const traverseNode = (node, pageId) => {
                    if (!node) return;
                    if (Array.isArray(node)) {
                        node.forEach((child) => traverseNode(child, pageId));
                        return;
                    }
                    if (typeof node === 'object') {
                        addVarsForDoubleBar(node, pageId);
                        // Dive into known containers
                        if (node.props) {
                            const p = node.props;
                            if (p.Data) traverseNode(p.Data, pageId);
                            if (p.items) traverseNode(p.items, pageId);
                            if (p.PageList) traverseNode(p.PageList, pageId);
                        }
                        if (node.content) traverseNode(node.content, pageId);
                        if (node.zones) {
                            Object.keys(node.zones).forEach((z) => traverseNode(node.zones[z], pageId));
                        }
                        // Generic object walk
                        Object.keys(node).forEach((k) => {
                            if (k !== 'type' && k !== 'props' && k !== 'zones' && k !== 'content') {
                                traverseNode(node[k], pageId);
                            }
                        });
                    }
                };
                // console.log(JSON.stringify(data__))
                // Traverse all nodes under content (handles containers like Columns/Wrapper)
                Object.keys(data__).forEach((element, index) => {
                    traverseNode(data__[element], data[i].roi_page_id);
                     const compType = data__[element]?.type;
                     // Track component type seen in content root
                     if (compType) componentTypesCollected.add(compType);
                     console.log('props', compType);

                     if (data__[element]?.type == 'InputTextField' ||
                         data__[element]?.type == 'InputNumberField' ||
                         data__[element]?.type == 'Range'
                    ) {
                         console.log('props123', data__[element]?.type);
                         componentTypesCollected.add(data__[element]?.type);
                        Object.keys(data__[element]?.props).forEach((element2, index2) => {
                            if (data__[element]?.props?.uuid != undefined) {

                                roi_global_arr[data__[element]?.props?.uuid] = {};
                                roi_global_arr[data__[element]?.props?.uuid]['count'] = 0;
                                roi_global_arr[data__[element]?.props?.uuid]['actions'] = data__[element]?.props?.buttonAction ?? '';
                                roi_global_arr[data__[element]?.props?.uuid]['action_id'] = data__[element]?.props?.buttonValue ?? '';
                                roi_global_arr[data__[element]?.props?.uuid]['link'] = data__[element]?.props?.buttonLink ?? '';
                                roi_global_arr[data__[element]?.props?.uuid]['value'] = data__[element]?.props?.value ?? '';
                                roi_global_arr[data__[element]?.props?.uuid]['default_value'] = data__[element]?.props?.default_value ?? '';
                                // roi_global_arr[data__[element]?.props?.uuid]['text'] = (data__[element]?.props?.buttonText ?? data__[element]?.props?.Text ?? data__[element]?.props?.Title ?? data__[element]?.props?.label ?? data__[element]?.props?.title ?? '') + ' / ' + data__[element]?.props?.uuid;
                                roi_global_arr[data__[element]?.props?.uuid]['text'] = (data__[element]?.props?.buttonText ?? data__[element]?.props?.Text ?? data__[element]?.props?.Title ?? data__[element]?.props?.label ?? data__[element]?.props?.title ?? '');
                    if (data__[element]?.type == 'InputTextField'
                    ) {
                                    roi_global_arr[data__[element]?.props?.uuid]['variable_data_type'] = 'string';
                                }

                    if (data__[element]?.type == 'InputNumberField') {
                                    roi_global_arr[data__[element]?.props?.uuid]['variable_data_type'] = 'number';
                                }

                    if (data__[element]?.type == 'Range') {
                        roi_global_arr[data__[element]?.props?.uuid]['variable_data_type'] = 'number';
                    }

                    // DoubleBarChart (content-level): create variables for Label (string) and Series B (number) + label-slug alias
                    if (data__[element]?.type == 'DoubleBarChart') {
                        componentTypesCollected.add('DoubleBarChart');
                        const chartId = data__[element]?.props?.id || 'DoubleBarChart';
                        const rows = data__[element]?.props?.Data || [];
                        rows.forEach((row, idx) => {
                            const labelText = (row && typeof row.Label === 'string' && row.Label.trim()) ? row.Label.trim() : `Bar ${idx + 1}`;
                            const labelKey = (row && row.LabelUuid) ? row.LabelUuid : `${chartId}-row-${idx}-label`;
                            const bKey = (row && row.BUuid) ? row.BUuid : `${chartId}-row-${idx}-b`;
                            const labelSlug = labelText
                                .toString()
                                .trim()
                                .replace(/\s+/g, '_')
                                .replace(/[^a-zA-Z0-9_]/g, '')
                                .toLowerCase();

                            // Label variable as string
                            roi_global_arr[labelKey] = {
                                currency: '0',
                                count: 0,
                                actions: '',
                                action_id: '',
                                link: '',
                                value: labelText,
                                default_value: '',
                                text: labelText,
                                variable_data_type: 'string',
                            };

                            // Series B numeric variable
                            const numericB = parseFloat(row?.B ?? 0) || 0;
                            roi_global_arr[bKey] = {
                                currency: '0',
                                count: 0,
                                actions: '',
                                action_id: '',
                                link: '',
                                value: numericB,
                                default_value: '',
                                text: `${labelText} (B)`,
                                variable_data_type: 'number',
                            };

                            // Alias variable where key is the label (slug) to mimic input field variable naming
                            if (labelSlug) {
                                roi_global_arr[labelSlug] = {
                                    currency: '0',
                                    count: 0,
                                    actions: '',
                                    action_id: '',
                                    link: '',
                                    value: numericB,
                                    default_value: '',
                                    text: labelText,
                                    variable_data_type: 'number',
                                };
                            }
                        });
                    }


                            }
                        });

                    } else {

                        Object.keys(data__[element]?.props).forEach((element2, index2) => {
                            if (data__[element]?.props[element2].uuid != undefined) {

                                if (element2 !== 'image' &&
                                    element2 !== 'logo' &&
                                    element2 !== 'items' &&
                                    element2 !== 'id' &&
                                    element2 !== 'Padding' &&
                                    element2 !== 'Style') {
                                    //console.log(element2);
                                    //console.log(data__[element]?.props[element2].uuid);
                                    roi_global_arr[data__[element]?.props[element2].uuid] = {};
                                    roi_global_arr[data__[element]?.props[element2].uuid]['count'] = 0;
                                    roi_global_arr[data__[element]?.props[element2].uuid]['actions'] = data__[element]?.props[element2].buttonAction ?? '';
                                    roi_global_arr[data__[element]?.props[element2].uuid]['action_id'] = data__[element]?.props[element2].buttonValue ?? '';
                                    roi_global_arr[data__[element]?.props[element2].uuid]['link'] = data__[element]?.props[element2].buttonLink ?? '';
                                    roi_global_arr[data__[element]?.props[element2].uuid]['value'] = data__[element]?.props?.[element2]?.value ?? '';
                                    roi_global_arr[data__[element]?.props[element2].uuid]['default_value'] = data__[element]?.props[element2].default_value ?? '';
                                    roi_global_arr[data__[element]?.props[element2].uuid]['text'] = (data__[element]?.props[element2].buttonText ?? data__[element]?.props[element2].Text ?? data__[element]?.props[element2].Title ?? data__[element]?.props[element2].label ?? data__[element]?.props[element2].title ?? '');
                                    if (data__[element]?.type == 'PageHeader'
                                        || data__[element]?.type == 'BannerLeftTextRightImage'
                                        || data__[element]?.type === 'Dropdown'
                                    ) {
                                        roi_global_arr[data__[element]?.props[element2].uuid]['variable_data_type'] = 'string';
                                    }

                                    if (data__[element]?.type == 'CardLayout1') {
                                        roi_global_arr[data__[element]?.props[element2].uuid]['variable_data_type'] = 'number';
                                    }

                                }
                            }
                        });
                    }

                });

                let data_zones__ = JSON.parse(data[i].structure)['zones'];
                Object.keys(data_zones__).forEach((element, index) => {
                    Object.keys(data_zones__[element]).forEach((element__, index__) => {
                        let data_element__ = data_zones__[element][element__];
                        traverseNode(data_element__, data[i].roi_page_id);
                        // console.log("data_element__", data_element__);
                        // console.log("data_element__", data_element__.props);
                        if (data_element__?.props.uuid != undefined) {
                            if (data_element__?.type === 'InputTextField' ||
                                data_element__?.type === 'InputNumberField' ||
                                data_element__?.type === 'Range' ||
                                data_element__?.type === 'Dropdown' ||
                                data_element__?.type === 'Button') {
                                componentTypesCollected.add(data_element__?.type);

                                roi_global_arr[data_element__?.props.uuid] = {};
                                roi_global_arr[data_element__?.props.uuid]['currency'] = data_element__?.props.prefix == 'currency' || data_element__?.props.prefix == 'Currency' || data_element__?.props.Prefix == 'Currency' || data_element__?.props.Prefix == 'currency' ? '1' : '0';
                                roi_global_arr[data_element__?.props.uuid]['count'] = 0;
                                roi_global_arr[data_element__?.props.uuid]['required'] = data_element__?.props.required ?? false;
                                roi_global_arr[data_element__?.props.uuid]['type'] = data_element__?.type ?? '';
                                roi_global_arr[data_element__?.props.uuid]['actions'] = data_element__?.props.buttonAction ?? '';
                                roi_global_arr[data_element__?.props.uuid]['action_id'] = data_element__?.props.buttonValue ?? '';
                                roi_global_arr[data_element__?.props.uuid]['link'] = data_element__?.props.buttonLink ?? '';
                                roi_global_arr[data_element__?.props.uuid]['value'] = data_element__?.props?.value ?? '';
                                roi_global_arr[data_element__?.props.uuid]['default_value'] = data_element__?.props.default_value ?? '';
                                roi_global_arr[data_element__?.props.uuid]['roi_page_id'] = data[i].roi_page_id;
                                roi_global_arr[data_element__?.props.uuid]['text'] = (data_element__?.props?.label ?? data_element__?.props?.buttonText ?? '');
                                if (data_element__?.type === 'Button'
                                    || data_element__?.type === 'Dropdown'
                                ) {
                                    roi_global_arr[data_element__?.props.uuid]['variable_data_type'] = 'string';

                                }
                                if (data_element__?.type === 'InputNumberField') {
                                    roi_global_arr[data_element__?.props.uuid]['variable_data_type'] = 'number';

                                }
                                if (data_element__?.type === 'InputTextField') {
                                    roi_global_arr[data_element__?.props.uuid]['variable_data_type'] = 'string';

                                }
                                if (data_element__?.type === 'Range') {
                                    roi_global_arr[data_element__?.props.uuid]['variable_data_type'] = 'number';

                                }
                                
                                // DoubleBarChart (zone-level): create variables for Label and Series B + label-slug alias
                                if (data_element__?.type === 'DoubleBarChart') {
                                    componentTypesCollected.add('DoubleBarChart');
                                    const chartId = data_element__?.props?.id || 'DoubleBarChart';
                                    const rows = data_element__?.props?.Data || [];
                                    rows.forEach((row, idx) => {
                                        const labelText = (row && typeof row.Label === 'string' && row.Label.trim()) ? row.Label.trim() : `Bar ${idx + 1}`;
                                        const labelKey = (row && row.LabelUuid) ? row.LabelUuid : `${chartId}-row-${idx}-label`;
                                        const bKey = (row && row.BUuid) ? row.BUuid : `${chartId}-row-${idx}-b`;
                                        const labelSlug = labelText
                                            .toString()
                                            .trim()
                                            .replace(/\s+/g, '_')
                                            .replace(/[^a-zA-Z0-9_]/g, '')
                                            .toLowerCase();

                                        roi_global_arr[labelKey] = {
                                            currency: '0',
                                            count: 0,
                                            required: false,
                                            type: 'DoubleBarChart-Label',
                                            actions: '',
                                            action_id: '',
                                            link: '',
                                            value: labelText,
                                            default_value: '',
                                            roi_page_id: data[i].roi_page_id,
        							        text: labelText,
                                            variable_data_type: 'string',
                                        };

                                        const numericB = parseFloat(row?.B ?? 0) || 0;
                                        roi_global_arr[bKey] = {
                                            currency: '0',
                                            count: 0,
                                            required: false,
                                            type: 'DoubleBarChart-B',
                                            actions: '',
                                            action_id: '',
                                            link: '',
                                            value: numericB,
                                            default_value: '',
                                            roi_page_id: data[i].roi_page_id,
                                            text: `${labelText} (B)`,
                                            variable_data_type: 'number',
                                        };

                                        if (labelSlug) {
                                            roi_global_arr[labelSlug] = {
                                                currency: '0',
                                                count: 0,
                                                required: false,
                                                type: 'DoubleBarChart-B',
                                                actions: '',
                                                action_id: '',
                                                link: '',
                                                value: numericB,
                                                default_value: '',
                                                roi_page_id: data[i].roi_page_id,
                                                text: labelText,
                                                variable_data_type: 'number',
                                            };
                                        }
                                    });
                                }
                            }



                            if (data_element__?.type === 'Checkbox') {
                                Object.keys(data_element__?.props?.items).forEach((element, index) => {
                                    if (data_element__?.props?.items[element]?.uuid != undefined) {

                                        roi_global_arr[data_element__?.props?.items[element]?.uuid] = {};
                                        roi_global_arr[data_element__?.props?.items[element]?.uuid]['currency'] = data_element__?.props?.items[element]?.prefix == 'currency' || data_element__?.props?.items[element]?.prefix == 'Currency' || data_element__?.props?.items[element]?.Prefix == 'currency' || data_element__?.props?.items[element]?.Prefix == 'Currency' ? '1' : '0';
                                        roi_global_arr[data_element__?.props?.items[element]?.uuid]['count'] = 0;
                                        roi_global_arr[data_element__?.props?.items[element]?.uuid]['actions'] = data_element__?.props?.items[element]?.buttonAction ?? '';
                                        roi_global_arr[data_element__?.props?.items[element]?.uuid]['action_id'] = data_element__?.props?.items[element]?.buttonValue ?? '';
                                        roi_global_arr[data_element__?.props?.items[element]?.uuid]['link'] = data_element__?.props?.items[element]?.buttonLink ?? '';
                                        roi_global_arr[data_element__?.props?.items[element]?.uuid]['value'] = data_element__?.props?.items[element]?.value ?? '';
                                        roi_global_arr[data_element__?.props?.items[element]?.uuid]['default_value'] = data_element__?.props?.items[element]?.default_value ?? '';
                                        roi_global_arr[data_element__?.props?.items[element]?.uuid]['text'] = (data_element__?.props?.items[element]?.Option ?? '');
                                        roi_global_arr[data_element__?.props?.items[element]?.uuid]['variable_data_type'] = 'number';


                                    }
                                });
                            }

                        }
                        if (data_element__?.props?.uuid == undefined && data_element__?.props?.items != undefined) {
                            Object.keys(data_element__?.props?.items).forEach((element, index) => {
                                // console.log("data_element__?.props?.items[element]", data_element__?.props?.items[element]);
                                if (data_element__?.props?.items[element]?.uuid != undefined) { //this will also work in case of button in card section

                                    roi_global_arr[data_element__?.props?.items[element]?.uuid] = {};
                                    roi_global_arr[data_element__?.props?.items[element]?.uuid]['currency'] = data_element__?.props?.items[element]?.prefix == 'currency' || data_element__?.props?.items[element]?.prefix == 'Currency' ? '1' : '0';
                                    roi_global_arr[data_element__?.props?.items[element]?.uuid]['count'] = 0;
                                    roi_global_arr[data_element__?.props?.items[element]?.uuid]['actions'] = data_element__?.props?.items[element]?.buttonAction ?? '';
                                    roi_global_arr[data_element__?.props?.items[element]?.uuid]['action_id'] = data_element__?.props?.items[element]?.buttonValue ?? '';
                                    roi_global_arr[data_element__?.props?.items[element]?.uuid]['link'] = data_element__?.props?.items[element]?.buttonLink ?? '';
                                    roi_global_arr[data_element__?.props?.items[element]?.uuid]['value'] = data_element__?.props?.items[element]?.value ?? '';
                                    roi_global_arr[data_element__?.props?.items[element]?.uuid]['default_value'] = data_element__?.props?.items[element]?.default_value ?? '';
                                    roi_global_arr[data_element__?.props?.items[element]?.uuid]['text'] = (data_element__?.props?.items[element]?.buttonText ?? data_element__?.props?.items[element]?.Text ?? data_element__?.props?.items[element]?.Title ?? data_element__?.props?.items[element]?.label ?? '');
                                    if (data_element__?.props?.items[element]?.prefix == 'currency' || data_element__?.props?.items[element]?.prefix == 'Currency' |
                                        data_element__?.props?.items[element]?.prefix == 'number' || data_element__?.props?.items[element]?.prefix == 'percentage') {
                                        roi_global_arr[data_element__?.props?.items[element]?.uuid]['variable_data_type'] = 'number';
                                    }



                                }

                                if (data_element__?.props?.items[element]?.uuid == undefined &&
                                    (data_element__?.type === 'DownloadReportTable' || data_element__?.type === 'ReportTable')
                                ) {
                                    // console.log("element", element)
                                    Object.keys(data_element__?.props?.items[element]).forEach((element_, index_) => {
                                        // console.log("data_element__?.props?.items[element]?.items[element_]", data_element__?.props?.items[element]?.items[element_]);

                                        let cols_count = data_element__?.props?.Table.Columns;
                                        for (let i = 1; i <= cols_count; i++) {
                                            if (data_element__?.props?.items[element]?.['Cell' + i + 'uuid'] != undefined) {

                                                roi_global_arr[data_element__?.props?.items[element]?.['Cell' + i + 'uuid']] = {};
                                                roi_global_arr[data_element__?.props?.items[element]?.['Cell' + i + 'uuid']]['currency'] = (data_element__?.props?.items[element]?.['Cell' + i + 'EditStyle']) == 'Currency' || (data_element__?.props?.items[element]?.['Cell' + i + 'EditStyle']) == 'currency' ? '1' : '0';
                                                roi_global_arr[data_element__?.props?.items[element]?.['Cell' + i + 'uuid']]['count'] = 0;
                                                roi_global_arr[data_element__?.props?.items[element]?.['Cell' + i + 'uuid']]['actions'] = data_element__?.props?.items[element]?.['Cell' + i + 'Action'] ?? '';
                                                roi_global_arr[data_element__?.props?.items[element]?.['Cell' + i + 'uuid']]['action_id'] = data_element__?.props?.items[element]?.['Cell' + i + 'ActionId'] ?? '';
                                                roi_global_arr[data_element__?.props?.items[element]?.['Cell' + i + 'uuid']]['link'] = '';
                                                roi_global_arr[data_element__?.props?.items[element]?.['Cell' + i + 'uuid']]['value'] = data_element__?.props?.items[element]?.['Cell' + i + 'Data'] ?? '';
                                                roi_global_arr[data_element__?.props?.items[element]?.['Cell' + i + 'uuid']]['default_value'] = data_element__?.props?.items[element]?.['Cell' + i + 'Data'] ?? '';
                                                roi_global_arr[data_element__?.props?.items[element]?.['Cell' + i + 'uuid']]['text'] = (data_element__?.props?.items[element]?.['Cell1Data'] + '-' + data_element__?.props?.Headings?.['Cell' + i]);

                                                if (data_element__?.props?.items[element]?.['Cell' + i + 'Style'] == 'number' ||
                                                    data_element__?.props?.items[element]?.['Cell' + i + 'Style'] == 'percentage' ||
                                                    data_element__?.props?.items[element]?.['Cell' + i + 'Style'] == 'currency') {
                                                    roi_global_arr[data_element__?.props?.items[element]?.['Cell' + i + 'uuid']]['variable_data_type'] = 'number';
                                                }

                                            }
                                        }

                                    });
                                }

                                if (data_element__?.props?.items[element]?.Value1 != undefined && data_element__?.type === 'CardSection') {
                                    Object.keys(data_element__?.props?.items[element]).forEach((element_, index_) => {
                                        for (let i = 1; i <= 4; i++) {
                                            if (data_element__?.props?.items[element]?.['Value' + i + 'uuid'] != undefined) {
                                                roi_global_arr[data_element__?.props?.items[element]?.['Value' + i + 'uuid']] = {};
                                                roi_global_arr[data_element__?.props?.items[element]?.['Value' + i + 'uuid']]['currency'] = '1';
                                                roi_global_arr[data_element__?.props?.items[element]?.['Value' + i + 'uuid']]['count'] = 0;
                                                roi_global_arr[data_element__?.props?.items[element]?.['Value' + i + 'uuid']]['actions'] = '';
                                                roi_global_arr[data_element__?.props?.items[element]?.['Value' + i + 'uuid']]['action_id'] = '';
                                                roi_global_arr[data_element__?.props?.items[element]?.['Value' + i + 'uuid']]['link'] = '';
                                                roi_global_arr[data_element__?.props?.items[element]?.['Value' + i + 'uuid']]['value'] = data_element__?.props?.items[element]?.['Value' + i] ?? '';
                                                roi_global_arr[data_element__?.props?.items[element]?.['Value' + i + 'uuid']]['default_value'] = '';
                                                roi_global_arr[data_element__?.props?.items[element]?.['Value' + i + 'uuid']]['text'] = (data_element__?.props?.items[element]?.['ValueTitle' + i] ?? '');
                                                roi_global_arr[data_element__?.props?.items[element]?.['Value' + i + 'uuid']]['variable_data_type'] = 'number';
                                            }
                                        }
                                    });
                                }
                            });

                        }


                        if (data_element__?.props?.uuid == undefined && data_element__?.props?.items == undefined) {
                            if (data_element__?.type === 'ResultCard') {
                                Object.keys(data_element__?.props).forEach((element, index) => {
                                    if (data_element__?.props?.[element]?.uuid != undefined) {

                                        roi_global_arr[data_element__?.props?.[element]?.uuid] = {};
                                        roi_global_arr[data_element__?.props?.[element]?.uuid]['currency'] = '1';
                                        roi_global_arr[data_element__?.props?.[element]?.uuid]['count'] = 0;
                                        roi_global_arr[data_element__?.props?.[element]?.uuid]['actions'] = data_element__?.props?.[element]?.buttonAction ?? '';
                                        roi_global_arr[data_element__?.props?.[element]?.uuid]['action_id'] = data_element__?.props?.[element]?.buttonValue ?? '';
                                        roi_global_arr[data_element__?.props?.[element]?.uuid]['link'] = data_element__?.props?.[element]?.buttonLink ?? '';
                                        roi_global_arr[data_element__?.props?.[element]?.uuid]['value'] = data_element__?.props?.[element]?.value ?? '';
                                        roi_global_arr[data_element__?.props?.[element]?.uuid]['default_value'] = data_element__?.props?.[element]?.default_value ?? '';
                                        roi_global_arr[data_element__?.props?.[element]?.uuid]['text'] = (data_element__?.props?.[element]?.title ?? '');
                                        roi_global_arr[data_element__?.props?.[element]?.uuid]['variable_data_type'] = 'number';


                                    }
                                });
                            }
                        }

                        if (data_element__?.type === 'ChartCard' || data_element__?.type === 'eCharts') {
                            let props = data_element__?.props;
                            Object.keys(props).forEach((element, index) => {
                                if (props[element]?.uuid != undefined && (props[element]?.chooseChart == 'pie' || props[element]?.chooseChart == 'bar')) {
                                    for (let i = 0; i < props[element]?.items?.length; i++) {
                                        //console.log("chart element uuid", props[element]?.items[i].uuid);
                                        roi_global_arr[props[element]?.items[i].uuid] = {};
                                        roi_global_arr[props[element]?.items[i].uuid]['currency'] = '1';
                                        roi_global_arr[props[element]?.items[i].uuid]['count'] = 0;
                                        roi_global_arr[props[element]?.items[i].uuid]['actions'] = props[element]?.items[i].buttonAction ?? '';
                                        roi_global_arr[props[element]?.items[i].uuid]['action_id'] = props[element]?.items[i].buttonValue ?? '';
                                        roi_global_arr[props[element]?.items[i].uuid]['link'] = props[element]?.items[i].buttonLink ?? '';
                                        roi_global_arr[props[element]?.items[i].uuid]['value'] = props[element]?.items[i].Data ?? '';
                                        roi_global_arr[props[element]?.items[i].uuid]['default_value'] = props[element]?.items[i].default_value ?? '';
                                        roi_global_arr[props[element]?.items[i].uuid]['text'] = (props[element]?.items[i].Text ?? '');
                                        roi_global_arr[props[element]?.items[i].uuid]['variable_data_type'] = 'number';
                                    }
                                }
                            });

                        }

                        if (data_element__?.type === "Radio") {
                            // Object.keys(data_element__?.props?.items).forEach(
                            //(element, index) => {
                            if (data_element__?.props?.uuid != undefined) {
                                roi_global_arr[data_element__?.props?.uuid] = {};
                                roi_global_arr[data_element__?.props?.uuid]["currency"] = data_element__?.props?.prefix == "currency" || data_element__?.props?.prefix == "Currency" || data_element__?.props?.Prefix == "currency" || data_element__?.props?.Prefix == "Currency" ? "1" : "0";
                                roi_global_arr[data_element__?.props?.uuid]["count"] = 0;
                                roi_global_arr[data_element__?.props?.uuid]["actions"] = data_element__?.props?.buttonAction ?? "";
                                roi_global_arr[data_element__?.props?.uuid]["action_id"] = data_element__?.props?.buttonValue ?? "";
                                roi_global_arr[data_element__?.props?.uuid]["link"] = data_element__?.props?.buttonLink ?? "";
                                roi_global_arr[data_element__?.props?.uuid]["value"] = "0";
                                roi_global_arr[data_element__?.props?.uuid]["default_value"] = data_element__?.props?.default_value ?? "";
                                roi_global_arr[data_element__?.props?.uuid]["text"] = data_element__?.props?.label ?? "";
                                roi_global_arr[data_element__?.props?.uuid]['variable_data_type'] = 'number';
                            }
                            //}
                            //);
                        }

                        if (data_element__?.type === "Wrapper") {
                            roi_global_arr[data_element__?.props?.uuid] = {};
                            roi_global_arr[data_element__?.props?.uuid]['currency'] = '0';
                            roi_global_arr[data_element__?.props?.uuid]['count'] = 0;
                            roi_global_arr[data_element__?.props?.uuid]['actions'] = '';
                            roi_global_arr[data_element__?.props?.uuid]['action_id'] = '';
                            roi_global_arr[data_element__?.props?.uuid]['link'] = '';
                            roi_global_arr[data_element__?.props?.uuid]['value'] = '';
                            roi_global_arr[data_element__?.props?.uuid]['text'] = '';
                            roi_global_arr[data_element__?.props.uuid]['default_value'] = data_element__?.props.default_value ?? '';
                            roi_global_arr[data_element__?.props.uuid]['roi_page_id'] = data[i].roi_page_id;
                            roi_global_arr[data_element__?.props.uuid]['text'] = (data_element__?.props?.label ?? '');
                            roi_global_arr[data_element__?.props.uuid]['variable_data_type'] = 'string';
                        }
                        // console.log("roi_global_arr", roi_global_arr);
                    });

                });
            }
            // console.log("step -1 roi_global_arr", roi_global_arr)
            // setRoipages(roi_content)

            //console.log("roipages", roipages);
            // dataStructure.result.forEach(() => )
            // setRenderData(parsedRenderData);

            const responseCustomVariables = await performHttpRequest(
                "GET",
                `${apiUrl}/roi-custom-variables/list/${id}`,
                ""
            );

            const dataCustomVariables = responseCustomVariables.data;
            if (dataCustomVariables.result == 'success') {
                let customVariablesArray = dataCustomVariables.data;
                //console.log("customVariablesArray", customVariablesArray);
                for (let i = 0; i < customVariablesArray.length; i++) {
                    roi_global_arr[customVariablesArray[i].roi_custom_variable_id] = {};
                    roi_global_arr[customVariablesArray[i].roi_custom_variable_id]['currency'] = (customVariablesArray[i].roi_custom_variable_type == 'currency' ? '1' : '0');
                    roi_global_arr[customVariablesArray[i].roi_custom_variable_id]['count'] = 0;
                    roi_global_arr[customVariablesArray[i].roi_custom_variable_id]['actions'] = '';
                    roi_global_arr[customVariablesArray[i].roi_custom_variable_id]['action_id'] = '';
                    roi_global_arr[customVariablesArray[i].roi_custom_variable_id]['link'] = '';
                    roi_global_arr[customVariablesArray[i].roi_custom_variable_id]['value'] = customVariablesArray[i].roi_custom_variable_value;
                    roi_global_arr[customVariablesArray[i].roi_custom_variable_id]['text'] = customVariablesArray[i].roi_custom_variable_name;
                    roi_global_arr[customVariablesArray[i].roi_custom_variable_id]['default_value'] = '';

                }
            }
            // Log all component types that contributed variables
            try {
                console.log('[ROI-Admin] Variable component types:', Array.from(componentTypesCollected));
            } catch (e) {}
            // console.log("step roi_global_arr", roi_global_arr)
            const schemaResponse=await performHttpRequest('GET',`${apiUrl}/roi-agent/schema/${id}`,'');
            const schema=schemaResponse.data?.schema;
            for(const field of schema?.fields||[])if(field.uuid && !['Wrapper','Columns','TransparentSection'].includes(field.type))roi_global_arr[field.uuid]={...(roi_global_arr[field.uuid]||{}),text:field.label&&field.id&&field.label!==field.id?field.label+' · '+field.id:field.label||field.id||field.uuid,variable_data_type:typeof field.default_value==='number'||['Range','InputNumberField','Checkbox Option'].includes(field.type)?'number':roi_global_arr[field.uuid]?.variable_data_type||'string',default_value:field.default_value,value:field.current_value};
            for(const variable of schema?.custom_variables||[])roi_global_arr[variable.roi_custom_variable_id]={...(roi_global_arr[variable.roi_custom_variable_id]||{}),text:variable.roi_custom_variable_name,variable_data_type:variable.roi_custom_variable_type==='string'?'string':'number',default_value:variable.roi_custom_variable_value};
            delete roi_global_arr.undefined;
            setRoiActionArr(roi_global_arr)

        } catch (error) {
            console.log(error);
        }
    }

    const getRoiCalculatedFieldDetail = async () => {
        try {
            // console.log(`${apiUrl}/roi-calculated-fields/detail/${cid}`)
            const response = await performHttpRequest(
                "GET",
                `${apiUrl}/roi-calculated-fields/detail/${cid}`,
                ""
            );
            if (response.data?.statusCode === 200 && response.data?.result == 'success') {
                // console.log("cid data ", response.data?.data[0])
                setLogicName(response.data?.data[0].roi_logic_name);
                let roi_calculation_logic = JSON.parse(response.data?.data[0].roi_calculation_logic);
                setWhen(roi_calculation_logic.when);
                setWhenAndOrOperator(roi_calculation_logic.when_and_or_operator);
                setThen(roi_calculation_logic.then);
            }
        } catch (error) {
            toast.error(error?.response?.data?.responseText);
        }
    };

    const onChangeLogicName = async (e) => {
        setLogicName(e.target.value)
        clearLogicNameError();
    }

    //-----------------When-----------------------------
    const addWhen = useCallback(async () => {

        let when__ = [...when];
        when__[when.length] = {};
        when__[when.length]['variable_selected_option'] = '';
        when__[when.length]['variable'] = '';
        when__[when.length]['operator'] = '';
        when__[when.length]['value'] = '';
        when__[when.length]['variable_data_type'] = 'number';
        when__[when.length]['value_type'] = 'static';
        setWhen(when__);
        
        // Add empty error object for new condition
        setWhenErrors([...whenErrors, {}]);
    }, [when, whenErrors]);

    const removeObjectAtIndexWhen = (index) => {
        const when__ = [...when];
        when__.splice(index, 1);
        setWhen(when__);
        
        // Remove error object for deleted condition
        const newErrors = [...whenErrors];
        newErrors.splice(index, 1);
        setWhenErrors(newErrors);
    };

    const removeWhen = async (index) => {
        removeObjectAtIndexWhen(index)
    }

    const onChangeWhenVariable = async (e, index) => {
        let when__ = [...when];
        when__[index]['variable_selected_option'] = e;
        when__[index]['variable'] = e?.value||'';
        when__[index]['variable_data_type'] = roiActionArr[e?.value]?.variable_data_type||'number';
        setWhen(when__);
        clearWhenError(index, 'variable');
    }

    const onChangeWhenOperator = async (e, index) => {
        let when__ = [...when];
        when__[index]['operator'] = e?.value||'';
        setWhen(when__);
        clearWhenError(index, 'operator');
    }

    const onChangeWhenValue = async (e, index) => {
        let when__ = [...when];
        when__[index]['value'] = e.target.value;
        setWhen(when__);
        clearWhenError(index, 'value');
    }

    const onChangeWhenValueString = async (e, index) => {
        let when__ = [...when];
        when__[index]['value_selected_option'] = e;
        when__[index]['value'] = e?.value||'';
        setWhen(when__);
        clearWhenError(index, 'value');
    }

    const onChangeWhenValueType = async (e, index) => {
        if(!e)return;
        let when__ = [...when];
        when__[index]['value_type'] = e.value;
        setWhen(when__);
        clearWhenError(index, 'value');
    }

    //-----------------When Operator-----------------------------
    const onChangeWhenAndOrOperator = async (e) => {
        setWhenAndOrOperator(e?.value||'AND')
    }

    //-----------------Then-----------------------------

    const addThen = async (index) => {
        let then__ = [...then];
        then__ = {};
        then__['action'] = '';
        then__['variable'] = '';
        then__['operator'] = '';
        then__['value'] = '';
        then__['variable_data_type'] = 'number';
        then__['value_type'] = 'static';
        insertObjectAtIndexThen(index, then__);
        
        // Add empty error object for new action
        const newErrors = [...thenErrors];
        newErrors.splice(index + 1, 0, {});
        setThenErrors(newErrors);
    }

    const insertObjectAtIndexThen = (index, newObject) => {
        const then__ = [...then];
        then__.splice(index + 1, 0, newObject);
        setThen(then__);
    };

    const onChangeThenAction = async (e, index) => {
        let then__ = [...then];
        then__[index]['action'] = e?.value||'';
        then__[index]['action_label'] = e?.label||'';
        setThen(then__);
        clearThenError(index, 'action');
    }

    const onChangeThenVariable = async (e, index) => {
        let then__ = [...then];
        then__[index]['variable_selected_option'] = e;
        then__[index]['variable'] = e?.value||'';
        setThen(then__);
        clearThenError(index, 'variable');
    }

    const onChangeThenValueVariable = async (e, index) => {
        let then__ = [...then];
        then__[index]['value_variable_selected_option'] = e;
        then__[index]['value_variable'] = e?.value||'';
        setThen(then__);
        clearThenError(index, 'value_variable');
    }

    const onChangeThenOperator = async (e, index) => {
        let then__ = [...then];
        then__[index]['operator'] = e?.value||'';
        setThen(then__);
        clearThenError(index, 'operator');
    }

    const onChangeThenValueType = async (e, index) => {
        if(!e)return;
        let then__ = [...then];
        if(then__[index].value_type!==e.value)then__[index].value=e.value==='expression'?{literal:0}:'';
        then__[index]['value_type'] = e.value;
        setThen(then__);
        clearThenError(index, 'value');
    }

    const onChangeThenValueString = async (e, index) => {
        let then__ = [...then];
        then__[index]['value_selected_option'] = e;
        then__[index]['value'] = e?.value||'';
        setThen(then__);
        clearThenError(index, 'value');
    }

    const onChangeThenValueNumber = async (e, index) => {
        let then__ = [...then];
        then__[index]['value'] = e.target.value;
        setThen(then__);
        clearThenError(index, 'value');
    }

    const removeObjectAtIndexThen = (index) => {
        const then__ = [...then];
        then__.splice(index, 1);
        setThen(then__);
        
        // Remove error object for deleted action
        const newErrors = [...thenErrors];
        newErrors.splice(index, 1);
        setThenErrors(newErrors);
    };

    const removeThen = async (index) => {
        removeObjectAtIndexThen(index)
    }

    //-----------------Sybmit-----------------------------
    const handleSubmit = async () => {
        // Validate form before submitting
        if (!validateForm()) {
            toast.error('Please fix the validation errors before submitting');
            return;
        }

        // console.log(logicName)
        //console.log(when)
        // console.log(whenAndOrOperator)
        // console.log(then)

        const updatedWhenArray = when.map(item => {
            const { variable_selected_option, ...rest } = item;
            return rest;
        });

        const updatedThenArray = then.map(item => {
            const { value_selected_option, variable_selected_option, ...rest } = item;
            return rest;
        });

        let finalArr = { "when": updatedWhenArray, "when_and_or_operator": whenAndOrOperator, "then": updatedThenArray }
        if (cid == undefined || cid == '' || cid == null) {
            let post_arr = {
                "roi_logic_name": logicName,
                "roi_id": id,
                "roi_calculation_logic": finalArr,
                "is_active": "1",
                "added_by": userId
            }
            const response = await performHttpRequest(
                "POST",
                `${apiUrl}/roi-calculated-fields/add`,
                post_arr
            );
            //console.log("add response", response.data.result);
            if (response.data.result == 'success') {
                toast.success("Added Successfully")
                navigate(`/admin/roi/${id}/calculation-logic`)
            }
        } else {
            let update_arr = {
                "roi_logic_name": logicName,
                "roi_calculated_field_id": cid,
                "roi_calculation_logic": finalArr
            }
            const response = await performHttpRequest(
                "POST",
                `${apiUrl}/roi-calculated-fields/update`,
                update_arr
            );
            //console.log("update response", response.data.result);
            if (response.data.result == 'success') {
                toast.success("Updated Successfully")
            }
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            if (cid === undefined || cid === '' || cid === null) {

            } else {
                await getRoiCalculatedFieldDetail();
            }

            await getRoiVariables();
        };

        fetchData();
    }, [cid]);

    useEffect(() => {
        // console.log("roiActionArr", roiActionArr)
        let options = Object.keys(roiActionArr).map((item, index) => ({
            value: item,
            label: roiActionArr[item].text
        }));


        let when__ = [...when];
        when.map((item, index) => {
            let preSelectedOption = options.find(option => option.value === when__[index].variable)|| (item.variable?{value:item.variable,label:'Unavailable field: '+item.variable}:null);
            when__[index]['variable_selected_option'] = preSelectedOption;
            if(item.value_type==='dynamic')when__[index].value_selected_option=options.find(option=>option.value===item.value)||{value:item.value,label:'Unavailable field: '+item.value};
            if (when__[index].value_type == undefined || when__[index].value_type == null) {
                when__[index]['value_type'] = 'static';
            }
        })
        setWhen(when__);


        let then__ = [...then];
        then.map((item, index) => {
            let preSelectedOption = options.find(option => option.value === then__[index].variable)|| (item.variable?{value:item.variable,label:'Unavailable field: '+item.variable}:null);
            then__[index]['variable_selected_option'] = preSelectedOption;
            if (then__[index].value_type === 'dynamic') {
                let preSelectedOption2 = options.find(option => option.value === then__[index].value)|| (item.value?{value:item.value,label:'Unavailable field: '+item.value}:null);
                then__[index]['value_selected_option'] = preSelectedOption2;
            }
        })

        // console.log(then__)
        setThen(then__);
    }, [roiActionArr]);

    // Initialize error arrays when when/then arrays change
    useEffect(() => {
        setWhenErrors(when.map(() => ({})));
    }, [when]);

    useEffect(() => {
        setThenErrors(then.map(() => ({})));
    }, [then]);

    return (
        <>
            <style>
                {`
                    .validation-msg {
                        font-size: 12px;
                        margin-top: 4px;
                        margin-bottom: 8px;
                    }
                    .custom-select.is-invalid {
                        border-color: #dc3545;
                    }
                    .custom-select.is-invalid .select__control {
                        border-color: #dc3545;
                    }
                    .custom-select.is-invalid .select__control:hover {
                        border-color: #dc3545;
                    }
                `}
            </style>
            <div className="d-flex flex-column main-outer-wrapper main-site-wrapper">
                <SurveyListHeader />
                <div id="viewport" className="d-flex">
                    <SurveyListSidebar />
                    <div className="flex-grow-1 d-flex">
                        <div className="flex-grow-1 variable-pg-content logic-editor-page">
                            {/* breadcrumbs here */}
                            <div className="custom-breadcrumb">
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb">
                                        <li className="breadcrumb-item"><Link to="/admin/roi" className="home-item" title="Dashboard">Home</Link></li>
                                        <li className="breadcrumb-item"><Link to="/admin/roi">Calculator</Link></li>
                                        {roiName && (
                                            <li className="breadcrumb-item">
                                                <Link to={`/admin/roi/${id}`}>{roiName}</Link>
                                            </li>
                                        )}
                                        {roiName && (
                                            <li className="breadcrumb-item">
                                                <Link to={`/admin/roi/${id}/calculation-logic`}>Calculations & Logics</Link>
                                            </li>
                                        )}
                                        <li className="breadcrumb-item active" aria-current="page">
                                            <strong>{cid ? "Edit calculation logic" : "Add calculation logic"}</strong>
                                        </li>
                                    </ol>
                                </nav>
                            </div>
                            {/* breadcrumbs ends here */}
                            <div className="survey-main-container">

                                <div className="pagesRow varPg">
                                    <div className="role-wrapper flex-grow-1">

                                        <div className="create-team-form">
                                            <div className="row gx-12px">
                                                <div className="col-lg-12 position-relative">
                                                    <div className="form-group mb-12px">
                                                        <label><strong>Logic name</strong></label>
                                                        <Input
                                                            type="text"
                                                            view="outlined"
                                                            dense={false}
                                                            name="logic_name"
                                                            color="default"
                                                            label=""
                                                            placeholder="Logic name"
                                                            clearButton={true}
                                                            success={false}
                                                            error={!!logicNameError}
                                                            fill={true}
                                                            errorHelperText={logicNameError}
                                                            className={"formsinput"}
                                                            onChange={onChangeLogicName}
                                                            value={logicName}
                                                        />
                                                    </div>
                                                    {logicNameError && (
                                                        <div className="validation-msg text-danger">
                                                            {logicNameError}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="row gx-12px">
                                                <div className="col-lg-12 position-relative">
                                                    <div className="form-group mb-12px">
                                                        <label><strong>When</strong></label>
                                                        {when.length===0&&<><RuleTriggerSummary steps={then} variables={roiActionArr}/><button type="button" className="btn btn-outline-secondary mt-2" onClick={addWhen}>Add trigger condition</button></>}
                                                        {(when.length > 0) && when.map((item, index) => {
                                                            console.log("item", item.variable_data_type);
                                                            return <>
                                                                <div className=" d-flex " key={item.variable}>
                                                                    <div className="col-lg-4 m-2">
                                                                        <label className="m-2">Condition Field</label>
                                                                        <Select
                                                                            className={`custom-select ${whenErrors[index]?.variable ? 'is-invalid' : ''}`}
                                                                            classNamePrefix="select"
                                                                            placeholder="Value Type"
                                                                            isClearable={true}
                                                                            isSearchable={true}
                                                                            name="value_type"
                                                                            options={Object.keys(roiActionArr).map((item, index) => ({
                                                                                value: item,
                                                                                label: roiActionArr[item].text
                                                                            }))}


                                                                            value={when[index].variable_selected_option ? when[index].variable_selected_option : null}
                                                                            onChange={(e) => onChangeWhenVariable(e, index)}
                                                                        />
                                                                        {whenErrors[index]?.variable && (
                                                                            <div className="validation-msg text-danger mt-1">
                                                                                {whenErrors[index].variable}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="col-lg-2 m-2">
                                                                        <label className="m-2">Operator</label>
                                                                        {(item.variable_data_type == 'number') &&
                                                                            <Select
                                                                                className={`custom-select ${whenErrors[index]?.operator ? 'is-invalid' : ''}`}
                                                                                classNamePrefix="select"
                                                                                placeholder="Operator"
                                                                                isClearable={true}
                                                                                isSearchable={true}
                                                                                name="operator"
                                                                                options={[
                                                                                    { value: '==', label: '==' },
                                                                                    { value: '!=', label: '!=' },
                                                                                    { value: '>', label: '>' },
                                                                                    { value: '<', label: '<' },
                                                                                    { value: '>=', label: '≥' },
                                                                                    { value: '<=', label: '≤' },
                                                                                    { value: 'is empty', label: 'is empty' },
                                                                                    { value: 'is not empty', label: 'is not empty' },
                                                                                ]}
                                                                                value={item.operator ? { value: item.operator, label: item.operator } : null}
                                                                                onChange={(e) => onChangeWhenOperator(e, index)}
                                                                            />
                                                                        }

                                                                        {(item.variable_data_type == 'string') &&
                                                                            <Select
                                                                                className={`custom-select ${whenErrors[index]?.operator ? 'is-invalid' : ''}`}
                                                                                classNamePrefix="select"
                                                                                placeholder="Contains"
                                                                                isClearable={true}
                                                                                isSearchable={true}
                                                                                name="operator"
                                                                                options={[
                                                                                    { value: 'contains', label: 'Contains' },
                                                                                    { value: 'does not contain', label: 'Does not contain' },
                                                                                    { value: 'starts with', label: 'Starts with' },
                                                                                    { value: 'does not start with', label: 'Does not start with' },
                                                                                    { value: 'ends with', label: 'Ends with' },
                                                                                    { value: 'does not end with', label: 'Does not end with' },
                                                                                    { value: 'is', label: 'Is' },
                                                                                    { value: 'is not', label: 'Is not' },
                                                                                    { value: 'is empty', label: 'Is empty' },
                                                                                    { value: 'is not empty', label: 'Is not empty' }
                                                                                ]}
                                                                                value={item.operator ? { value: item.operator, label: item.operator } : null}
                                                                                onChange={(e) => onChangeWhenOperator(e, index)}
                                                                            />
                                                                        }
                                                                        {whenErrors[index]?.operator && (
                                                                            <div className="validation-msg text-danger mt-1">
                                                                                {whenErrors[index].operator}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="col-lg-2 m-2">
                                                                        <label className="m-2">Value Type</label>
                                                                        <Select
                                                                            className="custom-select"
                                                                            classNamePrefix="select"
                                                                            placeholder="Value Type"
                                                                            isClearable={true}
                                                                            isSearchable={true}
                                                                            options={[
                                                                                { value: 'static', label: 'Static' },
                                                                                { value: 'dynamic', label: 'Dynamic' }
                                                                            ]}
                                                                            value={item.value_type ? { value: item.value_type, label: (item.value_type == 'static' ? 'Static' : item.value_type == 'dynamic' ? 'Dynamic' : '') } : null}
                                                                            onChange={(e) => onChangeWhenValueType(e, index)}
                                                                        />
                                                                    </div>
                                                                    <div className="col-lg-4 m-2">
                                                                        <label className="m-2">Value</label>
                                                                        {(item.value_type == 'static' || item.value_type == '' || item.value_type == null || item.value_type == undefined) &&
                                                                            <Input
                                                                                type="text"
                                                                                view="outlined"
                                                                                dense={false}
                                                                                name="when_value"
                                                                                color="default"
                                                                                label=""
                                                                                placeholder="Value..."
                                                                                clearButton={true}
                                                                                success={false}
                                                                                error={!!whenErrors[index]?.value}
                                                                                fill={true}
                                                                                errorHelperText={whenErrors[index]?.value}
                                                                                className={"formsinput"}
                                                                                value={item.value}
                                                                                onChange={(e) => onChangeWhenValue(e, index)}
                                                                            />
                                                                        }
                                                                        {(item.value_type == 'dynamic') &&
                                                                            <Select
                                                                                className={`custom-select ${whenErrors[index]?.value ? 'is-invalid' : ''}`}
                                                                                classNamePrefix="select"
                                                                                placeholder="Value"
                                                                                isClearable={true}
                                                                                isSearchable={true}
                                                                                options={Object.keys(roiActionArr).map((item, index) => ({
                                                                                    value: item,
                                                                                    label: roiActionArr[item].text
                                                                                }))}


                                                                                value={item.value_selected_option ? item.value_selected_option : null}
                                                                                onChange={(e) => onChangeWhenValueString(e, index)}
                                                                            />
                                                                        }
                                                                        {whenErrors[index]?.value && (
                                                                            <div className="validation-msg text-danger mt-1">
                                                                                {whenErrors[index].value}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {(index > 0) &&
                                                                        <div className="col-lg-1 m-2">
                                                                            <label className="m-2">Remove</label>
                                                                            <button type="button" className="btn btn-secondary" onClick={() => removeWhen(index)}>
                                                                                Remove
                                                                            </button>
                                                                        </div>
                                                                    }
                                                                    {((when).length - 1 == index) &&
                                                                        <div className="col-lg-1 m-2">
                                                                            <label className="m-2">Add</label>
                                                                            <button type="button" className="btn btn-primary" onClick={addWhen}>
                                                                                Add
                                                                            </button>
                                                                        </div>
                                                                    }
                                                                </div>

                                                            </>
                                                        })}
                                                    </div>
                                                    {/* {errors.team_name && (
                                                        <div className="validation-msg">
                                                            {errors.team_name}
                                                        </div>
                                                    )} */}
                                                </div>
                                            </div>
                                            {when.length>0&&<div className="row gx-12px">
                                                <div className="col-lg-3 position-relative">
                                                    <div className="form-group mb-12px">
                                                        <label><strong>When Operator</strong></label>
                                                        <div className="m-2">
                                                            <Select
                                                                className="custom-select"
                                                                classNamePrefix="select"
                                                                isClearable={true}
                                                                isSearchable={true}
                                                                name="conditions"
                                                                options={[
                                                                    { value: 'AND', label: 'AND' },
                                                                    { value: 'OR', label: 'OR' },
                                                                ]}
                                                                value={whenAndOrOperator ? { value: whenAndOrOperator, label: whenAndOrOperator } : null}
                                                                onChange={onChangeWhenAndOrOperator}
                                                            // value={firstSelectedCondition}
                                                            />
                                                        </div>
                                                    </div>
                                                    {/* {errors.team_name && (
                                                        <div className="validation-msg">
                                                            {errors.team_name}
                                                        </div>
                                                    )} */}
                                                </div>
                                            </div>}
                                            <div className="row gx-12px">
                                                <div className="col-lg-12 position-relative">
                                                    <div className="form-group mb-12px">
                                                        <label><strong>Then</strong></label>
                                                        {(then.length > 0) && then.map((item, index) => {
                                                            return <>

                                                                <div className="logic-action-row">

                                                                    <div className="col-lg-3 m-2 d-flex">
                                                                        {/* <div className="col-lg-2 m-2">
                                                                            <strong>And</strong>
                                                                        </div> */}
                                                                        <div className="col-lg-12">
                                                                            <label className="m-2">Action</label>
                                                                            <Select
                                                                                className={`custom-select ${thenErrors[index]?.action ? 'is-invalid' : ''}`}
                                                                                classNamePrefix="select"
                                                                                placeholder="Choose Action"
                                                                                isClearable={true}
                                                                                isSearchable={true}
                                                                                options={[
                                                                                    { value: 'calculate', label: 'Calculate' },
                                                                                    { value: 'show_blocks', label: 'Show Blocks' },
                                                                                    { value: 'hide_blocks', label: 'Hide Blocks' },
                                                                                    { value: 'show_error', label: 'Show Error' },
                                                                                    // { value: 'disable_item', label: 'disable_item' },
                                                                                    // { value: 'jump_to_page', label: 'jump_to_page' }
                                                                                ]}
                                                                                value={item.action ? { value: item.action, label: (item.action_label ? item.action_label : item.action) } : null}
                                                                                onChange={(e) => onChangeThenAction(e, index)}
                                                                            />
                                                                            {thenErrors[index]?.action && (
                                                                                <div className="validation-msg text-danger mt-1">
                                                                                    {thenErrors[index].action}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    <div className="col-lg-4 m-2">
                                                                        <label className="m-2">Variable</label>
                                                                        <Select
                                                                            className={`custom-select ${thenErrors[index]?.variable ? 'is-invalid' : ''}`}
                                                                            classNamePrefix="select"
                                                                            placeholder="Choose Variable"
                                                                            isClearable={true}
                                                                            isSearchable={true}
                                                                            options={Object.keys(roiActionArr).map((item, index) => ({
                                                                                value: item,
                                                                                label: roiActionArr[item].text
                                                                            }))}

                                                                            value={item.variable_selected_option ? item.variable_selected_option : null}
                                                                            onChange={(e) => onChangeThenVariable(e, index)}
                                                                        />
                                                                        {thenErrors[index]?.variable && (
                                                                            <div className="validation-msg text-danger mt-1">
                                                                                {thenErrors[index].variable}
                                                                            </div>
                                                                        )}
                                                                    </div>


                                                                    {/* </div>
                                                                <div className="d-flex"> */}
                                                                    {(item.action == 'calculate') &&
                                                                        <>
                                                                            <div className="col-lg-2 m-2">
                                                                                <label className="m-2">Operator</label>
                                                                                <Select
                                                                                    className={`custom-select ${thenErrors[index]?.operator ? 'is-invalid' : ''}`}
                                                                                    classNamePrefix="select"
                                                                                    placeholder="Choose Operator"
                                                                                    isClearable={true}
                                                                                    isSearchable={true}
                                                                                    options={[
                                                                                        { value: 'assign', label: 'assign' },
                                                                                        { value: 'add', label: 'add' },
                                                                                        { value: 'subtract', label: 'subtract' },
                                                                                        { value: 'multiply', label: 'multiply' },
                                                                                        { value: 'divide', label: 'divide' },
                                                                                    ]}
                                                                                    value={item.operator ? { value: item.operator, label: item.operator } : null}
                                                                                    onChange={(e) => onChangeThenOperator(e, index)}
                                                                                />
                                                                                {thenErrors[index]?.operator && (
                                                                                    <div className="validation-msg text-danger mt-1">
                                                                                        {thenErrors[index].operator}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                            <div className="col-lg-2 m-2">
                                                                                <label className="m-2">Value Type</label>
                                                                                <Select
                                                                                    className="custom-select"
                                                                                    classNamePrefix="select"
                                                                                    placeholder="Value Type"
                                                                                    isClearable={true}
                                                                                    isSearchable={true}
                                                                                    options={[
                                                                                        { value: 'static', label: 'static' },
                                                                                        { value: 'dynamic', label: 'dynamic' },
                                                                                        { value: 'expression', label: 'Expression (formula)' }
                                                                                    ]}
                                                                                    value={item.value_type ? { value: item.value_type, label: item.value_type } : null}
                                                                                    onChange={(e) => onChangeThenValueType(e, index)}
                                                                                />
                                                                            </div>
                                                                        </>
                                                                    }
                                                                    {(item.action == 'calculate' || item.action == 'show_error') &&
                                                                        <div className="logic-value-panel">
                                                                            <label className="m-2">Value</label>
                                                                            {item.value_type==='expression' && <ExpressionEditor value={item.value} variables={roiActionArr} onChange={value=>{setThen(current=>current.map((step,i)=>i===index?{...step,value}:step));clearThenError(index,'value');}}/>}
                                                                            {(item.value_type == 'static') &&
                                                                                <Input
                                                                                    type="text"
                                                                                    view="outlined"
                                                                                    dense={false}
                                                                                    color="default"
                                                                                    label=""
                                                                                    placeholder="Value..."
                                                                                    clearButton={true}
                                                                                    success={false}
                                                                                    error={!!thenErrors[index]?.value}
                                                                                    fill={true}
                                                                                    errorHelperText={thenErrors[index]?.value}
                                                                                    className={"formsinput"}
                                                                                    value={item.value}
                                                                                    onChange={(e) => onChangeThenValueNumber(e, index)}
                                                                                />
                                                                            }
                                                                            {(item.value_type == 'dynamic') &&
                                                                                <Select
                                                                                    className={`custom-select ${thenErrors[index]?.value ? 'is-invalid' : ''}`}
                                                                                    classNamePrefix="select"
                                                                                    placeholder="Value"
                                                                                    isClearable={true}
                                                                                    isSearchable={true}
                                                                                    options={Object.keys(roiActionArr).map((item, index) => ({
                                                                                        value: item,
                                                                                        label: roiActionArr[item].text
                                                                                    }))}


                                                                                    value={item.value_selected_option ? item.value_selected_option : null}
                                                                                    onChange={(e) => onChangeThenValueString(e, index)}
                                                                                />
                                                                            }
                                                                            {thenErrors[index]?.value && (
                                                                                <div className="validation-msg text-danger mt-1">
                                                                                    {thenErrors[index].value}
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                    }
                                                                    {(item.action == 'show_error') &&
                                                                        <div className="col-lg-4 m-2">
                                                                            <label className="m-2">Value Variable</label>
                                                                            <Select
                                                                                className={`custom-select ${thenErrors[index]?.value_variable ? 'is-invalid' : ''}`}
                                                                                classNamePrefix="select"
                                                                                placeholder="Choose Value Variable"
                                                                                isClearable={true}
                                                                                isSearchable={true}
                                                                                options={Object.keys(roiActionArr).map((item, index) => ({
                                                                                    value: item,
                                                                                    label: roiActionArr[item].text
                                                                                }))}

                                                                                value={item.value_variable_selected_option ? item.value_variable_selected_option : null}
                                                                                onChange={(e) => onChangeThenValueVariable(e, index)}
                                                                            />
                                                                            {thenErrors[index]?.value_variable && (
                                                                                <div className="validation-msg text-danger mt-1">
                                                                                    {thenErrors[index].value_variable}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    }

                                                                    <div className="col-lg-1 m-2">
                                                                        <label className="m-2">Remove</label>
                                                                        <button type="button" className="btn btn-secondary" onClick={() => removeThen(index)}>
                                                                            Remove
                                                                        </button>
                                                                    </div>
                                                                    <div className="col-lg-1 m-2">
                                                                        <label className="m-2">Add</label>
                                                                        <button type="button" className="btn btn-primary" onClick={() => addThen(index)}>
                                                                            Add
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                            </>
                                                        })}
                                                    </div>
                                                    {/* {errors.team_name && (
                                                        <div className="validation-msg">
                                                            {errors.team_name}
                                                        </div>
                                                    )} */}
                                                </div>
                                            </div>
                                            <div className="button-wrap">
                                                <button type="button " className="btn btn-primary" onClick={handleSubmit}>
                                                    Save
                                                </button>
                                                {/* Remove 'disabled' once input fields are filled whihh means the button should look like below once filled*/}
                                                {/* <button type="button" className="btn btn-primary">Save</button> */}

                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>


                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
